<script setup lang="ts">
import { computed } from 'vue'
import type { AvatarFit, AvatarShape, AvatarSize, AvatarTone } from './avatar-types'

const props = withDefaults(
  defineProps<{
    /** Person or entity name; used to derive `initials` (first two words) when it is not set. */
    name: string
    src?: string
    initials?: string
    /** Diameter on the shared scale: `sm` (32px) · `md` (48px, default). */
    size?: AvatarSize
    /** Fill when no image is set: `muted` (default) neutral tile · `strong` for on-brand surfaces · `inverted` solid ink · `linkedin` the platform blue. */
    tone?: AvatarTone
    /** Outline: `circle` (default) for people · `square` for brands. */
    shape?: AvatarShape
    /** Image fit: `cover` (default) · `contain` keeps the whole image on a lined tile. */
    fit?: AvatarFit
  }>(),
  {
    src: undefined,
    initials: undefined,
    size: 'md',
    tone: 'muted',
    shape: 'circle',
    fit: 'cover',
  },
)

const derivedInitials = computed(() => {
  if (props.initials) return props.initials
  return props.name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')
})

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'size-12 text-caption-emphasized',
  sm: 'size-16 text-caption-emphasized',
  md: 'size-24 text-body-emphasized',
}

const TONE_CLASSES: Record<AvatarTone, string> = {
  muted: 'bg-bg-tertiary text-text-secondary',
  strong: 'bg-bg-secondary text-text-primary',
  inverted: 'bg-bg-inverted text-text-inverted',
  linkedin: 'bg-platform-linkedin text-text-on-accent',
}
</script>

<template>
  <span
    aria-hidden="true"
    :class="[
      'relative grid shrink-0 place-items-center overflow-hidden',
      shape === 'square' ? 'rounded-1' : 'rounded-full',
      SIZE_CLASSES[size],
      !src && TONE_CLASSES[tone],
      src && fit === 'contain' && 'bg-bg-primary',
    ]"
  >
    <img
      v-if="src"
      :src="src"
      alt=""
      :class="fit === 'contain' ? 'size-full object-contain' : 'size-full object-cover'"
    />
    <span
      v-if="src && fit === 'contain'"
      class="pointer-events-none absolute inset-0 rounded-1 ring-1 ring-inset ring-border"
    />
    <slot v-else-if="!src"
      ><span>{{ derivedInitials }}</span></slot
    >
  </span>
</template>
